import _ from "lodash";
import React, { useState, useMemo, Fragment } from "react";
import classes from "./ListModal.scss";
import { useI18n } from "Hooks";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import EmptyView from "UIComponents/EmptyView/EmptyView";
import I18nHOC from "UIComponents/I18nHOC";
import { TagOutlined, CloseCircleFilled } from "@toddle-edu/ds-icons";
import {
  SearchInput,
  RadioButton,
  Button,
  Tooltip,
  Modal,
} from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";
import { PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION } from "Teacher/routes/Projects/Constants/stringConstants";

const ModalComponent = props => {
  const {
    options,
    value,
    modalTitleLabel,
    modalSearchPlaceholder,
    onClose,
    updateValue,
    projectGroupType,
    testId,
  } = props;
  const [selectedOption, setSelectedOption] = useState(value);
  const [searchText, setSearchText] = useState("");
  const { t } = useI18n(["common"]);

  const newOptions = useMemo(() => {
    return _.filter(options, option =>
      _.includes(_.toLower(option.label), _.toLower(searchText))
    );
  }, [options, searchText]);

  const onClickOption = value => {
    setSelectedOption(value);
  };

  const onDoneClick = () => {
    updateValue(selectedOption, { nodes: options });
    onClose();
  };

  return (
    <Modal onClose={onClose} width="600px" height="530px" dsVersion="2.0">
      <Modal.Header
        title={modalTitleLabel}
        hasSeparator={true}
        testId={testId}
      />
      <Modal.Body isScrollable={true}>
        <div className={classes.modalContainer}>
          {projectGroupType === PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION ? (
            <div className={classes.search}>
              <SearchInput
                placeholder={modalSearchPlaceholder}
                onChange={setSearchText}
                value={searchText}
                size="medium"
                dsVersion="2.0"
                focusSearchBySlash={true}
                testId={testId}
              />
            </div>
          ) : null}
          <div className={classes.innerContainer}>
            <div>
              {_.size(newOptions) > 0 ? (
                <>
                  {_.map(newOptions, option => {
                    return (
                      <div
                        className={classes.listItemContainer}
                        key={option.value}
                        onClick={() => onClickOption(option.value)}
                      >
                        <span
                          className={classNames("text-body", classes.title)}
                          dangerouslySetInnerHTML={{
                            __html: option?.label,
                          }}
                        />
                        <RadioButton
                          checked={selectedOption === option.value}
                          dsVersion="2.0"
                          size="medium"
                          testId={`${testId}-${option.value}`}
                        />
                      </div>
                    );
                  })}
                </>
              ) : (
                <EmptyView
                  emptyImageUrl={NoSearchResultsIllustration}
                  size={"small"}
                  emptyText={t("project:no_results_found_empty_text")}
                  emptyHeaderText={t("common:noResultHeading")}
                  dsVersion="2.0"
                />
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer hasSeparator>
        <div className={classes.footerContainer}>
          <Button
            onClick={onClose}
            variant="neutral"
            size="medium"
            type="outlined"
            dsVersion="2.0"
            testId={`${testId}-cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            disabled={!selectedOption}
            onClick={onDoneClick}
            variant="primary"
            type="fill"
            dsVersion="2.0"
            shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
            size="medium"
            testId={`${testId}-done`}
          >
            {t("common:done")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

class ListModal extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      showModal: false,
    };
  }

  toggleModal = () => {
    if (this.props?.isBoxDisabled) {
      return;
    }
    const { showModal } = this.state;
    this.setState({ showModal: !showModal });
  };

  renderEdit = () => {
    const {
      options,
      value,
      modalTitleLabel,
      modalSearchPlaceholder,
      disabledTooltip,
      t,
      isBoxDisabled,
      projectGroupType,
      testId,
    } = this.props;
    const { showModal } = this.state;

    const valueLabel = _.get(
      _.find(options, option => option.value === value),
      "label",
      null
    );

    const valueLabelTextContainerClasses = classNames({
      [classes.valueLabelText]: true,
      [classes.disabledContainer]: isBoxDisabled,
    });

    const labelContainerClass = classNames({
      [classes.valueLabelSpanContainer]: true,
      "text-body": true,
    });

    const handleDelete = e => {
      if (this.props?.isBoxDisabled) {
        return;
      }
      e.stopPropagation();
      this.updateValue("", { nodes: options });
    };

    const getBoxInnerComponent = () => (
      <span onClick={this.toggleModal}>{modalTitleLabel}</span>
    );

    return (
      <Fragment>
        {valueLabel ? (
          <div
            className={valueLabelTextContainerClasses}
            onClick={this.toggleModal}
            data-test-id={`${testId}-value`}
          >
            <span
              className={labelContainerClass}
              dangerouslySetInnerHTML={{ __html: valueLabel }}
            />
            {!isBoxDisabled && (
              <CloseCircleFilled variant={"subtle"} onClick={handleDelete} />
            )}
          </div>
        ) : (
          <Button
            onClick={this.toggleModal}
            disabled={isBoxDisabled}
            type="outlined"
            variant="neutral"
            icon={<TagOutlined />}
            testId={`${testId}-choose`}
          >
            {isBoxDisabled ? (
              <Tooltip tooltip={t(disabledTooltip)} isVisible={!isBoxDisabled}>
                {getBoxInnerComponent()}
              </Tooltip>
            ) : (
              getBoxInnerComponent()
            )}
          </Button>
        )}
        {showModal && (
          <ModalComponent
            options={options}
            value={value}
            updateValue={this.updateValue}
            modalTitleLabel={modalTitleLabel}
            modalSearchPlaceholder={modalSearchPlaceholder}
            onClose={this.toggleModal}
            projectGroupType={projectGroupType}
            testId={testId}
          />
        )}
      </Fragment>
    );
  };
}

ListModal.defaultProps = {
  ...UIBaseComponent.defaultProps,
  commentIconSize: "medium",
  testId: "listModal",
};

const mapStateToProps = (state, ownProps) => {
  return {
    isBoxDisabled: ownProps?.isBoxDisabled
      ? true
      : ownProps?.shouldBeDisabledOnEmpty && _.isEmpty(ownProps?.options),
  };
};

export default compose(
  I18nHOC({ resource: ["project"] }),
  connect(mapStateToProps)
)(ListModal);
